export interface AuthCookieData {
  token: string
  refreshToken: string
  locationId: string
  userId?: string
  contactId: string
  externalUserId?: string
  sessionUniqueKey?: string
}

/**
 * Tolerant base64-JSON decoder for the `catv2` auth cookie.
 *
 * Writer contract: `btoa(JSON.stringify(data))` (see `setAuthCookie` in
 * `cookie.utils.ts`). Server middleware reads the raw value from request
 * headers via `readRawAuthCookieFromEvent` and passes it here.
 *
 * Returns `null` for any decode/parse/shape failure so the caller can clear
 * the bad cookie and continue as unauthenticated. Raw-JSON shapes set by
 * sibling services on the apex domain are intentionally rejected — they must
 * conform to the base64-JSON contract.
 *
 * Never throws.
 */
function decodeBase64ToUtf8(rawValue: string): string {
  if (typeof atob !== 'undefined') {
    return atob(rawValue)
  }

  return Buffer.from(rawValue, 'base64').toString('utf-8')
}

export function decodeBase64JsonAuth(rawValue: string): AuthCookieData | null {
  if (!rawValue) {
    return null
  }
  try {
    const decoded = decodeBase64ToUtf8(rawValue)
    const parsed = JSON.parse(decoded) as Partial<AuthCookieData>
    if (!parsed || typeof parsed !== 'object') {
      return null
    }
    if (typeof parsed.token !== 'string' || parsed.token.length === 0) {
      return null
    }
    if (typeof parsed.contactId !== 'string' || parsed.contactId.length === 0) {
      return null
    }
    return parsed as AuthCookieData
  } catch {
    return null
  }
}
